import React from 'react';
import { snapshot_MetricCard as MetricCard } from './snapshot_MetricCard';
import { snapshot_ChartCard as ChartCard } from './snapshot_ChartCard';
import { snapshot_BarChart as BarChart } from './snapshot_BarChart';
import { snapshot_DonutChart as DonutChart } from './snapshot_DonutChart';
import { useSnapshotData } from '../hooks/useSnapshotData';
import { Place } from '@/types';
import { ProcessedMetrics } from '@/types';
import { SnapshotRowProvider } from '../contexts/SnapshotRowContext';
import { useShareUrl } from '@/hooks/useShareUrl';
import { formatPlaceName } from '@/lib/utils';

interface SnapshotDashboardProps {
  selectedPlace: Place;
  selectedPlaceMetrics: ProcessedMetrics[];
  peerMetrics: { peer: Place; data: ProcessedMetrics[] }[];
}

export const snapshot_Dashboard: React.FC<SnapshotDashboardProps> = ({
  selectedPlace,
  selectedPlaceMetrics,
  peerMetrics
}) => {
  const snapshotData = useSnapshotData(selectedPlace, selectedPlaceMetrics, peerMetrics);
  const shareLink = useShareUrl();
  
  // Debug logging for metric data
  console.log('[Dashboard] Snapshot data:', snapshotData);
  if (snapshotData?.metrics) {
    console.log('[Dashboard] Population metric:', snapshotData.metrics.population);
    console.log('[Dashboard] Housing stock metric:', snapshotData.metrics.housingStock);
    console.log('[Dashboard] Unbuilt permits metric:', snapshotData.metrics.unbuiltPermits);
  }

  if (!snapshotData) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <p>No data available</p>
      </div>
    );
  }

  return (
    <main id="snapshot-charts-section" className="bg-neutral-100 flex flex-col max-md:px-5">
      <SnapshotRowProvider>
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 py-12 sm:py-16 md:py-20">
          {/* First row - Metric cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <MetricCard
              title="Total Population"
              description={`Estimated population of ${formatPlaceName(selectedPlace.name)} as of ${selectedPlaceMetrics[0].year}`}
              changePercent={snapshotData.metrics.population.changePercent}
              changeDescription="YoY"
              changeType={snapshotData.metrics.population.changeType}
              value={snapshotData.metrics.population.value}
              valueUnit={snapshotData.metrics.population.unit}
              rowIndex={0}
              cardIndex={0}
              chartName="Total Population"
              shareLink={shareLink}
            />
            <MetricCard
              title="Housing Stock Potential"
              description={`Number of housing units if all permitted units were built instantly in ${formatPlaceName(selectedPlace.name)} as of ${selectedPlaceMetrics[0].year}`}
              changePercent={snapshotData.metrics.housingStock.changePercent}
              changeDescription="instant growth in 2023"
              changeType={snapshotData.metrics.housingStock.changeType}
              value={snapshotData.metrics.housingStock.value}
              valueUnit={snapshotData.metrics.housingStock.unit}
              valueUnitDescription="total units once built"
              rowIndex={0}
              cardIndex={1}
              chartName="Housing Stock Potential"
              shareLink={shareLink}
            />
            <MetricCard
              title="Unbuilt Permitted Units"
              description={`Number of units that are not yet recorded as built in ${formatPlaceName(selectedPlace.name)} as of ${selectedPlaceMetrics[0].year}`}
              changePercent={snapshotData.metrics.unbuiltPermits.changePercent}
              changeDescription="YoY"
              changeType={snapshotData.metrics.unbuiltPermits.changeType}
              value={snapshotData.metrics.unbuiltPermits.value}
              valueUnit={snapshotData.metrics.unbuiltPermits.unit}
              rowIndex={0}
              cardIndex={2}
              chartName="Unbuilt Permitted Units"
              shareLink={shareLink}
            />
          </div>

          {/* Second row - Chart cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <ChartCard
              title={snapshotData.charts.populationGrowth.title}
              description={snapshotData.charts.populationGrowth.description}
              rowIndex={1}
              cardIndex={0}
              chartName="Population Growth"
              shareLink={shareLink}
            >
              <BarChart 
                data={snapshotData.charts.populationGrowth.data}
                unit="percent"
                scaleConfig={{
                  padding: 0.1,
                  tickCount: 5,
                  includeZero: true,
                  dataType: 'percentage'
                }}
                yAxisLabel="% Change YoY"
              />
            </ChartCard>
            <ChartCard
              title={snapshotData.charts.housingStockGrowth.title}
              description={snapshotData.charts.housingStockGrowth.description}
              rowIndex={1}
              cardIndex={1}
              chartName="Housing Stock Growth"
              shareLink={shareLink}
            >
              <BarChart 
                data={snapshotData.charts.housingStockGrowth.data}
                unit="percent"
                scaleConfig={{
                  min: 0,
                  padding: 0.1,
                  tickCount: 5,
                  enforceMinZero: true,
                  dataType: 'percentage'
                }}
                yAxisLabel="% Change in Units"
              />
            </ChartCard>
          </div>

          {/* Third row - Metric cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            <MetricCard
              title="Price to Income Ratio"
              description={`Median home value to median household income ratio in ${formatPlaceName(selectedPlace.name)} as of ${selectedPlaceMetrics[0].year}`}
              changePercent={snapshotData.metrics.priceToIncome.changePercent}
              changeDescription="YoY"
              changeType={snapshotData.metrics.priceToIncome.changeType}
              value={snapshotData.metrics.priceToIncome.value}
              valueUnit={snapshotData.metrics.priceToIncome.unit}
              valueUnitDescription="home value : income"
              rowIndex={2}
              cardIndex={0}
              chartName="Price to Income Ratio"
              shareLink={shareLink}
            />
            <MetricCard
              title="Rental Vacancy Rate"
              description={`Percentage of rental units that are vacant in ${formatPlaceName(selectedPlace.name)} as of ${selectedPlaceMetrics[0].year}`}
              changePercent={snapshotData.metrics.rentalVacancy.changePercent}
              changeDescription="YoY"
              changeType={snapshotData.metrics.rentalVacancy.changeType}
              value={snapshotData.metrics.rentalVacancy.value}
              valueUnit={snapshotData.metrics.rentalVacancy.unit}
              rowIndex={2}
              cardIndex={2}
              chartName="Rental Vacancy Rate"
              shareLink={shareLink}
              />
            <MetricCard
              title="Multi Family Share"
              description={`Percentage of housing units in multi-family (2+ unit) structures in ${formatPlaceName(selectedPlace.name)} as of ${selectedPlaceMetrics[0].year}`}
              changePercent={snapshotData.metrics.multiFamilyShare.changePercent}
              changeDescription="YoY"
              changeType={snapshotData.metrics.multiFamilyShare.changeType}
              value={snapshotData.metrics.multiFamilyShare.value}
              valueUnit={snapshotData.metrics.multiFamilyShare.unit}
              valueUnitDescription="of total units"
              rowIndex={2}
              cardIndex={1}
              chartName="Multi Family Share"
              shareLink={shareLink}
            />
          </div>

          {/* Fourth row - Chart cards */}
          <div className="grid grid-cols-1 donut:grid-cols-2 gap-6 mt-6">
            <ChartCard
              title={snapshotData.charts.housingStockByDecade.title}
              description={snapshotData.charts.housingStockByDecade.description}
              rowIndex={3}
              cardIndex={0}
              chartName="Housing Stock by Decade"
              shareLink={shareLink}
            >
              <BarChart 
                data={snapshotData.charts.housingStockByDecade.data}
                unit="units"
                scaleConfig={{
                  min: 0,
                  padding: 0.1,
                  tickCount: 6,
                  enforceMinZero: true
                }}
                yAxisLabel="Units"
                xAxisLabel="Decade Built"
                barSpacing="compact"
              />
            </ChartCard>
            <ChartCard
              title={snapshotData.charts.housingStockComposition.title}
              description={snapshotData.charts.housingStockComposition.description}
              rowIndex={3}
              cardIndex={1}
              chartName="Housing Stock Composition"
              shareLink={shareLink}
            >
              <DonutChart
                legendItems={snapshotData.charts.housingStockComposition.legendItems}
                totalHousingUnits={snapshotData.charts.housingStockComposition.totalHousingUnits}
              />
            </ChartCard>
          </div>
        </div>
      </SnapshotRowProvider>
    </main>
  );
};
